import { IMeeting } from "@mctypes/meeting.types";
import { IUserPartial } from "@schemas/issueSchema";
import { Flex } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import SplitPane from "react-split-pane";
import { useGetProjectByProjectIdMeetingAndMeetingIdQuery } from "../../../../../../openapi";
// import "react-split-pane/lib/styles.css";
import { FailedAlert, McTitle } from "../../../../../components";
import GlobalFileUploaderTable from "../../../../../components/FileUploader/GlobalFileUploaderTable";
import McInputNew from "../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../components/McInput/McTextAreaNew";
import { McMantineMultiUserSelect } from "../../../../../components/McMantineUserSelect/McMantineUserMultiSelect";
import { McMantineUserSelect } from "../../../../../components/McMantineUserSelect/McMantineUserSelect";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { getProjectIdFromUrl } from "../../../../utils";
import { useUpdateMeetingAgendaMutation } from "../../meetingAgendaRTK";
import { setAgendaSplitPaneWidthInRedux } from "../../meetingSlice";
import { MeetingDetails } from "../../meetingApiTypes";
import AgendaConnectedIssues from "./AgendaConnectedIssues";
import { AgendaItemComments } from "./AgendaItemComments";
import AgendaPrepTodoItems from "./AgendaPrepTodoItems";

const AgendaItem: React.FC<{ onAgendaItemChange: IMeeting.onAgendaItemChange; agendaItem: IMeeting.AgendaItem }> = ({
  onAgendaItemChange,
  agendaItem,
}) => {
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const meetingId = history.location.pathname.split("/")[4];
  const dispatch = useAppDispatch();
  const splitWidth = useAppSelector((state) => state.meetingReducer.agendaSplitPaneWidth);
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const [splitPaneHeight, setSplitPaneHeight] = useState<number | null>(null);

  const { data: singleMeetingResponse, isError: singleMeetingError } = useGetProjectByProjectIdMeetingAndMeetingIdQuery({
    meetingId,
    projectId,
  });
  const singleMeeting: MeetingDetails | undefined = singleMeetingResponse;

  const [updateAgendaItem, { isError: updateAgendaItemError }] = useUpdateMeetingAgendaMutation();

  const participantUsers = useMemo(
    () =>
      ((singleMeeting?.participants as IMeeting.ParticipantsPopulated[])?.map((p) => p.user) ?? []) as IUserPartial[],
    [singleMeeting?.participants],
  );

  const participantComboboxData = useMemo(
    () => participantUsers.map((user) => ({ value: user._id, label: user.name })),
    [participantUsers],
  );

  useEffect(() => {
    const updateHeight = () => {
      const leftHeight = leftPaneRef.current?.scrollHeight ?? 0;
      const rightHeight = rightPaneRef.current?.scrollHeight ?? 0;
      const nextHeight = Math.max(leftHeight, rightHeight);

      if (nextHeight > 0) {
        setSplitPaneHeight(nextHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    if (leftPaneRef.current) observer.observe(leftPaneRef.current);
    if (rightPaneRef.current) observer.observe(rightPaneRef.current);

    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const isError = singleMeetingError || updateAgendaItemError;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/** Use ref to capture latest drag size until finished */}
      <SplitPane
        split="vertical"
        minSize={450}
        maxSize={-450}
        defaultSize={`${splitWidth}%`}
        onDragFinished={(size) => {
          const containerWidth = document.querySelector(".Resizer")?.parentElement?.clientWidth;
          if (containerWidth) {
            const percent = (size / containerWidth) * 100;
            dispatch(setAgendaSplitPaneWidthInRedux(percent));
          }
        }}
        allowResize
        style={{ position: "relative", height: splitPaneHeight == null ? "auto" : `${splitPaneHeight}px` }}
        pane1Style={{ padding: "1rem" }}
        pane2Style={{ padding: "1rem" }}
        resizerStyle={{
          width: "3px",
          cursor: "col-resize",
          background: "#e2e6ea",
          transition: "background .15s",
        }}
      >
        {/* Left Pane */}
        <Flex ref={leftPaneRef} direction="column" gap="xs">
          {isError && <FailedAlert />}
          <McInputNew
            onBlur={(e) => onAgendaItemChange(agendaItem.title, e.target.value, "title")}
            value={agendaItem.title}
            styles={{ input: { fontSize: "1.4rem" } }}
            placeholder="Skriv inn tittel på agendapunkt"
          />
          <McTitle title="Beskrivelse">
            <McTextAreaNew
              onBlur={(e) => onAgendaItemChange(agendaItem.description, e.target.value, "description")}
              value={agendaItem.description}
              placeholder="Skriv inn beskrivelse"
              autosize
              spellCheck={false}
            />
          </McTitle>
          <McTitle title="Formål">
            <McTextAreaNew
              onBlur={(e) => onAgendaItemChange(agendaItem.purpose, e.target.value, "purpose")}
              value={agendaItem.purpose}
              placeholder="Skriv inn formål"
              autosize
            />
          </McTitle>
          <McTitle title="Konklusjon">
            <McTextAreaNew
              onBlur={(e) => onAgendaItemChange(agendaItem.conclusion, e.target.value, "conclusion")}
              value={agendaItem.conclusion}
              autosize
              spellCheck={false}
            />
          </McTitle>
          <McTitle title="Ansvarlig deltager">
            <div style={{ width: "300px" }}>
              <McMantineUserSelect
                data={participantComboboxData}
                value={
                  agendaItem.assignedTo == null
                    ? null
                    : typeof agendaItem.assignedTo === "string"
                    ? agendaItem.assignedTo
                    : agendaItem.assignedTo._id
                }
                placeholder="Velg deltager"
                onChange={(userId) => {
                  const nextUser = participantUsers.find((user) => user._id === userId) ?? null;
                  onAgendaItemChange(agendaItem.assignedTo, nextUser as any, "assignedTo");
                }}
              />
            </div>
          </McTitle>
          <McTitle title="Ansvarlig, annen">
            <div style={{ width: "300px" }}>
              <McInputNew
                onBlur={(e) => onAgendaItemChange(agendaItem.assignedToFreeText, e.target.value, "assignedToFreeText")}
                value={agendaItem.assignedToFreeText}
                type="text"
              />
            </div>
          </McTitle>
          <McTitle title="Deltakere">
            <div style={{ width: "300px" }}>
              <McMantineMultiUserSelect
                key={`${agendaItem._id}-participants-${(agendaItem?.itemParticipants ?? [])
                  .map((participant) => participant._id)
                  .join(",")}`}
                data={participantUsers}
                defaultValue={(agendaItem?.itemParticipants ?? []) as IUserPartial[]}
                placeholder="Velg deltakere"
                onChange={(users) => {
                  onAgendaItemChange(agendaItem?.itemParticipants, users as any, "itemParticipants");
                }}
              />
            </div>
          </McTitle>
          <AgendaPrepTodoItems agendaItem={agendaItem} />
          <McTitle title="Varighet (min)">
            <div style={{ width: "100px" }}>
              <McInputNew
                onBlur={(e) => onAgendaItemChange(agendaItem.duration, Number(e.target.value), "duration")}
                value={agendaItem.duration}
                type="number"
                min={1}
                max={180}
              />
            </div>
          </McTitle>
          <McTitle title="Vedlegg">
            <GlobalFileUploaderTable path={`${projectId}/meetingagenda/${agendaItem._id}/files`} />
          </McTitle>
        </Flex>
        {/* Right Pane */}
        <Flex ref={rightPaneRef} direction="column" gap="md">
          <AgendaConnectedIssues agendaItem={agendaItem} defaultIssueBoard={singleMeeting?.defaultIssueBoard} />
          <AgendaItemComments agendaItem={agendaItem} projectId={projectId} singleMeeting={singleMeeting} />
        </Flex>
      </SplitPane>
    </div>
  );
};

export default AgendaItem;
